import { useQuestionBuilder } from 'lib-node-question-builder';
import { useCallback } from 'react';

import { ModelsEnumGrantAssetAutomationStatus } from '~/__generated__/api';
import { LOAN_PRODUCT_TYPE_BY_NUMBER } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import useUrl from '~/common/hooks/useUrl';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';
import {
  ASSET_TYPE,
  DONOR_RELATIONSHIP_TYPE,
  GIFT_SOURCE_TYPE,
} from '~/pages/Loan1003DataGrids/common/components/asset1003Constants';
import { formatName } from '~/pages/Loan1003DataGrids/common/utils/utils';

import { BankStatementFieldArray } from '../components/BankStatementFieldArray';
import { READONLY_CONDITION } from '../constants';
import {
  useGetAssetsSummaryInformationQuery,
  useSaveAssetSummaryMutation,
} from '../hooks';
import { useAsset1003Store } from '../store';

type AssetFormProps = {
  assetId: number | null;
};

type AssetScreenData = {
  doesBankStatementExist: boolean;
  isEMDQuestionsAnswered: boolean;
  hasGiftSourceId: boolean;
  isNewAsset: boolean;
  isDonorNamePopulated: boolean;
  isNextLargestDepositVisible: boolean;
  isDeleted: boolean;
  associatedBorrowerOptions: { label: string; value: string }[];
  donorRelationshipOptions: { label: string; value: string }[];
  assetTypeOptions: { label: string; value: string }[];
  isWriteAccessDisabled: boolean;
  grantAssetAutomationStatus?: ModelsEnumGrantAssetAutomationStatus;
};

const EMD_TYPES = new Set([
  'Earnest Money Deposit',
  'Cash Deposit',
  'Net Proceeds',
]);
const GIFT_TYPES = new Set(['Gift', 'Gift of Equity']);

function isEMDCashDepositOrNetProceeds(assetType: string) {
  return EMD_TYPES.has(assetType);
}

function isGrant(assetType: string) {
  return assetType === 'Grant';
}

function isGiftOrGiftOfEquity(assetType: string) {
  return GIFT_TYPES.has(assetType);
}

export type BankStatementAssociationDetail = {
  _id?: string;
  userResponses?: {
    statementStartDate?: string;
    statementEndDate?: string;
    statementNumberOfDays?: number;
  };
};

export function AssetForm(props: Readonly<AssetFormProps>) {
  const { isReadOnly } = useUrl();
  const isWriteAccessDisabled = useIsWriteAccessDisabled() || isReadOnly;
  const user = useUser();
  const featureFlags = useNavFeatureFlags();
  const loanSummary = useGetLoanSummary();
  const underwritingMethod = loanSummary?.data?.loanInfo?.underwritingMethod;

  const { data } = useGetAssetsSummaryInformationQuery({
    select: useCallback(
      (s) => {
        const asset =
          s.transformedAssets.find((a) => a.assetId === props.assetId) ?? {};

        const doesBankStatementExist =
          asset.documentAssociationDetails?.some(
            (detail) =>
              detail.userResponses?.statementStartDate &&
              detail.userResponses?.statementEndDate,
          ) ?? false;

        const isEMDQuestionsAnswered =
          asset.userResponses?.emdSourceOption !== undefined ||
          asset.userResponses?.cashDepositSource !== undefined ||
          asset.userResponses?.netProceedsAddressId !== undefined;

        const hasGiftSourceId = asset.giftSourceId != null;
        const isNewAsset = props.assetId === null;
        const donorName = asset.giftInformation?.donorName ?? '';
        const nextLargestDeposit = asset.nextLargestDeposit ?? null;

        const loanProductTypeString =
          LOAN_PRODUCT_TYPE_BY_NUMBER[s.loanProductType] ?? null;

        return {
          assetId: asset.assetId ?? 0,
          assetLdId: isNewAsset ? 0 : (asset.assetLdId ?? 1),
          lenderDatabaseId: asset.customerLenderDatabaseId ?? 0,
          isEarnestMoneyDepositAGift:
            asset.giftInformation?.isEarnestMoneyDepositAGift ?? null,
          assetsFeatureFlags: s.assetsFeatureFlags,
          loanProductType: s.loanProductType,
          giftSourceId: asset.giftSourceId ?? null,
          loanProductTypeString,
          formData: {
            assetType: asset.assetType ?? '',
            bankName: asset.bankName ?? '',
            accountNumber: asset.accountNumber ?? '',
            balance: asset.balance,
            comment: asset.comment ?? '',
            customerRecordIds:
              asset.borrowers?.map((b) => String(b.customerRecordId)) ?? [],
            donorRelationship: asset.giftInformation?.donorRelationship ?? '',
            donorName,
            giftSourceDisplay: GIFT_SOURCE_TYPE[asset.giftSourceId] ?? '',
            nextLargestDeposit,
            bankStatementAssociationDetails: (
              asset.documentAssociationDetails ?? []
            ).map((d) => ({
              ...d,
              _id: crypto.randomUUID(),
            })) as BankStatementAssociationDetail[],
          },
          staticScreenData: {
            doesBankStatementExist,
            isEMDQuestionsAnswered,
            hasGiftSourceId,
            isNewAsset,
            isDonorNamePopulated: donorName !== '',
            isNextLargestDepositVisible:
              nextLargestDeposit !== null && loanProductTypeString !== 'VA',
            isDeleted: asset.isDeleted ?? false,
            productType: loanProductTypeString,
            grantAssetAutomationStatus: asset.grantAssetAutomationStatus,
            associatedBorrowerOptions: s.filteredBorrowers.map((borrower) => ({
              label: formatName(
                borrower.firstName,
                borrower.lastName,
                borrower.middleName,
                borrower.suffixName,
              ),
              value: String(borrower.customerRecordId),
            })),
          },
        };
      },
      [props.assetId],
    ),
  });

  const { mutate, isLoading } = useSaveAssetSummaryMutation();

  const isConventional = data?.loanProductTypeString === 'Conventional';
  const isVA = data?.loanProductTypeString === 'VA';
  const isLPA = underwritingMethod === 'Loan Product Advisor';
  const lpaFlagEnabled =
    data?.assetsFeatureFlags
      ?.AssetScreenGiftDonorDropdownEnhancementEffectiveDate ?? false;
  const duFlagEnabled =
    data?.assetsFeatureFlags
      ?.AssetScreenGiftUnRelatedIndividualDuUpdateEffectiveDate ?? false;
  const includeUnrelatedDonor =
    isConventional && ((lpaFlagEnabled && isLPA) || duFlagEnabled);

  const donorRelationshipOptions = includeUnrelatedDonor
    ? [
        ...DONOR_RELATIONSHIP_TYPE,
        {
          value:
            'Unrelated individual with close, family-like ties to the Borrower',
          label:
            'Unrelated individual with close, family-like ties to the Borrower',
        },
      ]
    : DONOR_RELATIONSHIP_TYPE;

  const isUnderwriter = user?.isUnderwriter ?? false;
  const isExpectedAssetsVisible =
    featureFlags?.AssetsScreenExpectedAssetsWorkflowEffectiveDate ?? false;

  const assetTypeOptions = ASSET_TYPE.filter(
    (x) =>
      (!isVA || x.value !== 'Gift of Equity') &&
      ((isUnderwriter && isExpectedAssetsVisible) ||
        x.value !== 'Expected Assets'),
  );

  const screenData: AssetScreenData = {
    ...(data?.staticScreenData ?? {
      doesBankStatementExist: false,
      isEMDQuestionsAnswered: false,
      hasGiftSourceId: false,
      isNewAsset: props.assetId === null,
      isDonorNamePopulated: false,
      isNextLargestDepositVisible: false,
      isDeleted: false,
      associatedBorrowerOptions: [],
    }),
    donorRelationshipOptions,
    assetTypeOptions,
    isWriteAccessDisabled,
  };

  const { form, handleSubmit } = useQuestionBuilder({
    formData: data?.formData,
    screenData,
    hooks: {
      onSubmit: (values) => {
        mutate({
          assetsFeatureFlags: data.assetsFeatureFlags,
          productType: data.loanProductTypeString,
          assetCustomerData: {
            assetType: values.assetType,
            bankName: values.bankName,
            accountNumber: values.accountNumber,
            balance: Number(values.balance),
            giftSourceId: data.giftSourceId,
            nextLargestDeposit: values.nextLargestDeposit,
            comment: values.comment,
            assetId: data.assetId,
            assetLdId: data.assetLdId,
            lenderDatabaseId: 1,
            customerRecordIds: values.customerRecordIds.map(Number),
            giftInformation: {
              donorName: values.donorName,
              donorRelationship: values.donorRelationship,
              isEarnestMoneyDepositAGift: data.isEarnestMoneyDepositAGift,
            },
            sendToAus: 'Y',
          },
        });
      },
    },
  });

  return (
    <form.Provider>
      <Loan1003FormContainer
        isLoading={!data || isLoading}
        readOnlyMode={isWriteAccessDisabled}
        onSaveClicked={() => {
          handleSubmit();
        }}
        onCloseClicked={() => {
          useAsset1003Store.setState({ activeForm: null });
        }}
      >
        <Loan1003FormSection title="Asset Information">
          <form.CheckboxGroup
            name="customerRecordIds"
            label="Associated Borrower(s)"
            rules={{ required: true }}
            options={screenData.associatedBorrowerOptions}
            conditions={{
              isDisabled: {
                or: [
                  {
                    when: 'doesBankStatementExist',
                    dataSource: 'screenData',
                    is: true,
                  },
                  READONLY_CONDITION,
                ],
              },
            }}
            hasBottomBorder={false}
            style={{ padding: '0 0 0 1rem' }}
          />
          <form.Grid
            name="asset information grid"
            columns={4}
            style={{ padding: '0 0 0 1rem' }}
          >
            <form.Select
              name="assetType"
              label="Asset Type"
              placeholder="Select option"
              listeners={{
                onChange: ({ fieldApi, value }) => {
                  const formApi = fieldApi.form;
                  if (!isGrant(value)) {
                    if (isEMDCashDepositOrNetProceeds(value)) {
                      formApi.setFieldValue('bankName', value);
                      formApi.setFieldValue('accountNumber', '');
                    } else {
                      formApi.setFieldValue('bankName', '');
                      formApi.setFieldValue('accountNumber', '');
                    }
                  }
                },
              }}
              rules={{
                required: true,
              }}
              options={assetTypeOptions}
              conditions={{
                isDisabled: {
                  or: [
                    {
                      when: 'isDeleted',
                      dataSource: 'screenData',
                      is: true,
                    },
                    READONLY_CONDITION,
                  ],
                },
              }}
            />
            <form.Text
              name="bankName"
              label="Bank Name"
              placeholder="Bank Name"
              conditions={{
                isVisible: {
                  and: [
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Earnest Money Deposit',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Cash Deposit',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Net Proceeds',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Gift',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Gift of Equity',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Expected Assets',
                    },
                    {
                      or: [
                        {
                          when: 'assetType',
                          dataSource: 'formData',
                          isNot: 'Grant',
                        },
                        {
                          and: [
                            {
                              when: 'hasGiftSourceId',
                              dataSource: 'screenData',
                              is: true,
                            },
                            {
                              when: 'isNewAsset',
                              dataSource: 'screenData',
                              is: false,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                isDisabled: {
                  or: [
                    READONLY_CONDITION,
                    {
                      when: 'doesBankStatementExist',
                      dataSource: 'screenData',
                      is: true,
                    },
                    {
                      and: [
                        {
                          when: 'assetType',
                          dataSource: 'formData',
                          is: 'Grant',
                        },
                        {
                          when: 'hasGiftSourceId',
                          dataSource: 'screenData',
                          is: true,
                        },
                      ],
                    },
                  ],
                },
              }}
            />
            <form.Text
              name="accountNumber"
              label="Account Number"
              placeholder="Account Number"
              conditions={{
                isVisible: {
                  and: [
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Earnest Money Deposit',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Cash Deposit',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Net Proceeds',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Gift',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Gift of Equity',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Grant',
                    },
                    {
                      when: 'assetType',
                      dataSource: 'formData',
                      isNot: 'Expected Assets',
                    },
                  ],
                },
                isDisabled: {
                  or: [
                    {
                      when: 'doesBankStatementExist',
                      dataSource: 'screenData',
                      is: true,
                    },
                    READONLY_CONDITION,
                  ],
                },
              }}
            />
            <form.Currency
              name="balance"
              label="Balance"
              placeholder="Balance"
              rules={{
                required: true,
                onChange: {
                  type: 'number',
                  gt: 0,
                  message: 'Amount must be greater than 0',
                },
              }}
              conditions={{
                isDisabled: {
                  or: [
                    READONLY_CONDITION,
                    {
                      when: 'doesBankStatementExist',
                      dataSource: 'screenData',
                      is: true,
                    },
                    {
                      and: [
                        {
                          when: 'assetType',
                          dataSource: 'formData',
                          is: 'Grant',
                        },
                        {
                          when: 'hasGiftSourceId',
                          dataSource: 'screenData',
                          is: true,
                        },
                      ],
                    },
                    {
                      and: [
                        {
                          or: [
                            {
                              when: 'assetType',
                              dataSource: 'formData',
                              is: 'Gift',
                            },
                            {
                              when: 'assetType',
                              dataSource: 'formData',
                              is: 'Gift of Equity',
                            },
                          ],
                        },
                        {
                          when: 'isDonorNamePopulated',
                          dataSource: 'screenData',
                          is: true,
                        },
                        {
                          when: 'isNewAsset',
                          dataSource: 'screenData',
                          is: false,
                        },
                      ],
                    },
                    {
                      and: [
                        {
                          when: 'isEMDQuestionsAnswered',
                          dataSource: 'screenData',
                          is: true,
                        },
                        {
                          or: [
                            {
                              when: 'assetType',
                              dataSource: 'formData',
                              is: 'Earnest Money Deposit',
                            },
                            {
                              when: 'assetType',
                              dataSource: 'formData',
                              is: 'Cash Deposit',
                            },
                            {
                              when: 'assetType',
                              dataSource: 'formData',
                              is: 'Net Proceeds',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      and: [
                        {
                          when: 'assetType',
                          dataSource: 'formData',
                          is: 'Expected Assets',
                        },
                        {
                          when: 'isNewAsset',
                          dataSource: 'screenData',
                          is: false,
                        },
                      ],
                    },
                  ],
                },
              }}
            />
          </form.Grid>
          {data?.staticScreenData?.doesBankStatementExist ? (
            <BankStatementFieldArray
              bankStatements={data.formData.bankStatementAssociationDetails}
            />
          ) : null}
        </Loan1003FormSection>
        <form.Subscribe selector={(state) => state.values.assetType}>
          {(assetType) => {
            const showDonorSection =
              !screenData.isNewAsset &&
              (isGiftOrGiftOfEquity(assetType) ||
                (assetType === 'Grant' && screenData.hasGiftSourceId));

            if (!showDonorSection) return null;

            const sourceLabel =
              assetType === 'Grant' ? 'Grant Source' : 'Gift Source';
            const isDonorLocked =
              isGiftOrGiftOfEquity(assetType) &&
              screenData.isDonorNamePopulated;

            return (
              <Loan1003FormSection title="Donor Information">
                <form.Grid
                  name="donor information grid"
                  columns={4}
                  style={{ padding: '0 0 0 1rem' }}
                >
                  <form.Select
                    name="donorRelationship"
                    label="Donor Relationship"
                    placeholder="Select option"
                    options={donorRelationshipOptions}
                    conditions={{
                      isVisible: {
                        and: [
                          {
                            when: 'productType',
                            dataSource: 'screenData',
                            isNot: 'VA',
                          },
                          {
                            or: [
                              {
                                when: 'assetType',
                                dataSource: 'formData',
                                is: 'Gift',
                              },
                              {
                                when: 'assetType',
                                dataSource: 'formData',
                                is: 'Gift of Equity',
                              },
                            ],
                          },
                        ],
                      },
                      isDisabled: { or: [isDonorLocked, READONLY_CONDITION] },
                    }}
                  />
                  <form.Text
                    name="giftSourceDisplay"
                    label={sourceLabel}
                    placeholder={sourceLabel}
                    conditions={{
                      isDisabled: true,
                    }}
                  />
                  <form.Text
                    name="donorName"
                    label="Donor Name"
                    placeholder="Donor Name"
                    conditions={{
                      isVisible: isDonorLocked,
                      isDisabled: true,
                    }}
                  />
                </form.Grid>
              </Loan1003FormSection>
            );
          }}
        </form.Subscribe>
        <Loan1003FormSection title="Loan Information">
          <form.Currency
            name="nextLargestDeposit"
            label="Next Largest Deposit"
            placeholder="Next Largest Deposit"
            conditions={{
              isVisible: {
                when: 'isNextLargestDepositVisible',
                dataSource: 'screenData',
                is: true,
              },
              isDisabled: {
                or: [
                  {
                    when: 'doesBankStatementExist',
                    dataSource: 'screenData',
                    is: true,
                  },
                  READONLY_CONDITION,
                ],
              },
            }}
            hasBottomBorder={false}
            style={{ padding: '0 0 0 1rem' }}
          />
          <form.Grid
            name="loan information comments grid"
            columns={4}
            style={{ padding: '0 0 0 1rem' }}
          >
            <form.GridItem colSpan={4}>
              <form.TextArea
                name="comment"
                label="Comments"
                placeholder="Comments"
                minRows={3}
                hasBottomBorder={false}
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
          </form.Grid>
        </Loan1003FormSection>
      </Loan1003FormContainer>
    </form.Provider>
  );
}
